import { taskParticipantIds, type Chat, type WorktreeDetailResponse } from "@rivets/core";

/**
 * Merges a create/handoff response without replacing a newer realtime snapshot of the same chat.
 * The API broadcasts `chat.updated` before responding, so the common path is already present.
 */
export function mergeCreatedChat(
  current: WorktreeDetailResponse | undefined,
  chat: Chat,
): WorktreeDetailResponse | undefined {
  if (!current || current.chats.some((candidate) => candidate.id === chat.id)) return current;
  return { ...current, chats: [chat, ...current.chats] };
}

/** Authorship only grows. A REST snapshot can race a realtime participant update. */
export function preserveChatParticipants(previous: Chat | undefined, incoming: Chat): Chat {
  const known = previous?.participantIds;
  if (!known?.length || known.every((id) => incoming.participantIds?.includes(id))) return incoming;
  return { ...incoming, participantIds: [...new Set([...(incoming.participantIds ?? []), ...known])].sort() };
}

export function preserveWorktreeParticipants(previous: WorktreeDetailResponse | undefined, incoming: WorktreeDetailResponse): WorktreeDetailResponse {
  if (!previous || previous.worktree.id !== incoming.worktree.id) return incoming;
  const known = new Map(previous.chats.map((chat) => [chat.id, chat]));
  return {
    ...incoming,
    participantIds: [...new Set([
      ...(previous.participantIds ?? []), ...taskParticipantIds(previous.worktree, previous.chats),
      ...(incoming.participantIds ?? []), ...taskParticipantIds(incoming.worktree, incoming.chats),
    ])],
    chats: incoming.chats.map((chat) => preserveChatParticipants(known.get(chat.id), chat)),
  };
}


export function applyParticipantUpdate(current: WorktreeDetailResponse | undefined, update: { chatId: string; participantIds: string[] }): WorktreeDetailResponse | undefined {
  if (!current) return current;
  return {
    ...current,
    participantIds: [...new Set([...(current.participantIds ?? []), ...update.participantIds])],
    chats: current.chats.map((chat) => chat.id === update.chatId
      ? preserveChatParticipants({ ...chat, participantIds: update.participantIds }, chat) : chat),
  };
}

/** Rename only owns the title. Never roll back another user's prompt/status. */
export function patchChatTitle(current: WorktreeDetailResponse | undefined, chatId: string, title: string, expectedTitle?: string): WorktreeDetailResponse | undefined {
  if (!current) return current;
  return { ...current, chats: current.chats.map((chat) => chat.id === chatId && (expectedTitle === undefined || chat.title === expectedTitle)
    ? { ...chat, title } : chat) };
}
